import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

export interface LiveSubscriptionConfig<Key, Entry, State> {
  /** Stable string identity for a key — subscriptions and ref-counts key off it. */
  keyOf(key: Key): string;
  /**
   * Open the live source for `key`. Call `emit(entry)` per live entry and
   * `ready()` once caught up. `reset()` starts a fresh transport replay after
   * reconnect. Return a teardown invoked on the last release.
   */
  connect(
    key: Key,
    emit: (entry: Entry) => void,
    ready: () => void,
    reset: () => void,
    fail: () => void,
  ): () => void;
  /** Fold one live entry into the accumulated snapshot state. */
  reduce(state: State, entry: Entry): State;
  /** Snapshot state for a freshly (re)opened subscription. */
  initial: State;
}

interface Store<Entry, State> {
  state: State;
  pending: State;
  isReady: boolean;
  isFailed: boolean;
  lastGoodAt?: number;
  refCount: number;
  listeners: Set<() => void>;
  entryListeners: Set<(entry: Entry) => void>;
  teardown: (() => void) | null;
  generation: number;
  notifyScheduled: boolean;
}

export interface LiveSubscriptionStore<Key, Entry, State> {
  /** Reduced snapshot + readiness for a key. Pass `null` to no-op. */
  useSnapshot(key: Key | null): {
    state: State;
    isReady: boolean;
    isFailed: boolean;
    lastGoodAt?: number;
    retry(): void;
  };
  /** Fire `onEntry` once per live entry for a key. Pass `null` to no-op. */
  useEntries(key: Key | null, onEntry: (entry: Entry) => void): void;
  apply(key: Key, entry: Entry): void;
}

const scheduleFrame: (cb: () => void) => void =
  typeof globalThis.requestAnimationFrame === "function"
    ? (cb) => {
        globalThis.requestAnimationFrame(() => cb());
      }
    : (cb) => {
        setTimeout(cb, 0);
      };

export function createLiveSubscriptionStore<Key, Entry, State>(
  config: LiveSubscriptionConfig<Key, Entry, State>,
): LiveSubscriptionStore<Key, Entry, State> {
  const stores = new Map<string, Store<Entry, State>>();

  function getStore(id: string): Store<Entry, State> {
    let store = stores.get(id);
    if (!store) {
      store = {
        state: config.initial,
        pending: config.initial,
        isReady: false,
        isFailed: false,
        refCount: 0,
        listeners: new Set(),
        entryListeners: new Set(),
        teardown: null,
        generation: 0,
        notifyScheduled: false,
      };
      stores.set(id, store);
    }
    return store;
  }

  function notify(store: Store<Entry, State>): void {
    for (const listener of store.listeners) listener();
  }

  function scheduleNotify(store: Store<Entry, State>): void {
    if (store.notifyScheduled) return;
    store.notifyScheduled = true;
    scheduleFrame(() => {
      store.notifyScheduled = false;
      store.state = store.pending;
      notify(store);
    });
  }

  function openSubscription(key: Key, store: Store<Entry, State>, preserveState = false): void {
    const generation = ++store.generation;
    if (!preserveState) store.state = config.initial;
    store.pending = preserveState ? store.state : config.initial;
    store.isReady = false;
    store.isFailed = false;

    const emit = (entry: Entry) => {
      if (store.generation !== generation) return;
      store.pending = config.reduce(store.pending, entry);
      if (store.isReady) {
        store.state = store.pending;
        store.lastGoodAt = Date.now();
        scheduleNotify(store);
      }
      for (const listener of store.entryListeners) listener(entry);
    };
    const ready = () => {
      if (store.generation !== generation) return;
      store.state = store.pending;
      store.isReady = true;
      store.isFailed = false;
      store.lastGoodAt = Date.now();
      notify(store);
    };
    const reset = () => {
      if (store.generation !== generation) return;
      store.pending = config.initial;
      store.isReady = false;
      store.isFailed = false;
      notify(store);
    };
    const fail = () => {
      if (store.generation !== generation) return;
      store.isFailed = true;
      notify(store);
    };

    let teardown: (() => void) | undefined;
    try {
      teardown = config.connect(key, emit, ready, reset, fail);
    } catch {
      if (store.generation === generation) {
        store.isFailed = true;
        notify(store);
      }
      return;
    }
    // A teardown that races an in-flight open (refCount hit 0 during connect)
    // is superseded — dispose it rather than leak a live connection.
    if (store.generation !== generation) {
      teardown();
      return;
    }
    store.teardown = teardown;
  }

  function closeSubscription(store: Store<Entry, State>): void {
    store.generation++;
    store.teardown?.();
    store.teardown = null;
    store.state = config.initial;
    store.pending = config.initial;
    store.isReady = false;
    store.isFailed = false;
    store.lastGoodAt = undefined;
  }

  function acquire(key: Key, id: string): Store<Entry, State> {
    const store = getStore(id);
    store.refCount++;
    if (store.refCount === 1) openSubscription(key, store);
    return store;
  }

  function release(id: string): void {
    const store = stores.get(id);
    if (!store) return;
    store.refCount--;
    if (store.refCount <= 0) {
      closeSubscription(store);
      queueMicrotask(() => {
        if (store.refCount <= 0 && stores.get(id) === store) stores.delete(id);
      });
    }
  }

  function retry(key: Key, id: string): void {
    const store = stores.get(id);
    if (!store || store.refCount <= 0) return;
    store.generation++;
    store.teardown?.();
    store.teardown = null;
    openSubscription(key, store, true);
    notify(store);
  }

  function useSnapshot(key: Key | null): {
    state: State;
    isReady: boolean;
    isFailed: boolean;
    lastGoodAt?: number;
    retry(): void;
  } {
    const id = key === null ? null : config.keyOf(key);
    const keyRef = useRef(key);
    keyRef.current = key;

    useEffect(() => {
      if (id === null) return;
      // keyRef holds the key that produced this id; id is the effect identity.
      acquire(keyRef.current as Key, id);
      return () => release(id);
    }, [id]);

    const subscribe = useCallback(
      (cb: () => void) => {
        if (id === null) return () => {};
        const store = getStore(id);
        store.listeners.add(cb);
        return () => {
          store.listeners.delete(cb);
        };
      },
      [id],
    );
    const getState = useCallback(
      () => (id === null ? config.initial : (stores.get(id)?.state ?? config.initial)),
      [id],
    );
    const getReady = useCallback(
      () => (id === null ? false : (stores.get(id)?.isReady ?? false)),
      [id],
    );
    const getFailed = useCallback(
      () => (id === null ? false : (stores.get(id)?.isFailed ?? false)),
      [id],
    );
    const getLastGoodAt = useCallback(
      () => (id === null ? undefined : stores.get(id)?.lastGoodAt),
      [id],
    );
    const retrySubscription = useCallback(() => {
      if (id !== null && keyRef.current !== null) retry(keyRef.current, id);
    }, [id]);

    const state = useSyncExternalStore(subscribe, getState, getState);
    const isReady = useSyncExternalStore(subscribe, getReady, getReady);
    const isFailed = useSyncExternalStore(subscribe, getFailed, getFailed);
    const lastGoodAt = useSyncExternalStore(subscribe, getLastGoodAt, getLastGoodAt);
    return { state, isReady, isFailed, lastGoodAt, retry: retrySubscription };
  }

  function useEntries(key: Key | null, onEntry: (entry: Entry) => void): void {
    const id = key === null ? null : config.keyOf(key);
    const keyRef = useRef(key);
    keyRef.current = key;
    // Latest callback via a ref so a new `onEntry` identity does not resubscribe.
    const callbackRef = useRef(onEntry);
    callbackRef.current = onEntry;
    const [stableListener] = useState(() => (entry: Entry) => callbackRef.current(entry));

    useEffect(() => {
      if (id === null) return;
      const store = acquire(keyRef.current as Key, id);
      store.entryListeners.add(stableListener);
      return () => {
        const current = stores.get(id);
        current?.entryListeners.delete(stableListener);
        release(id);
      };
    }, [id, stableListener]);
  }

  function apply(key: Key, entry: Entry): void {
    const store = stores.get(config.keyOf(key));
    if (!store) return;
    store.pending = config.reduce(store.pending, entry);
    store.state = store.isReady ? store.pending : config.reduce(store.state, entry);
    if (store.isReady) store.lastGoodAt = Date.now();
    notify(store);
    for (const listener of store.entryListeners) listener(entry);
  }

  return { useSnapshot, useEntries, apply };
}
